import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AUTH_TG_URL,
  AUTH_URL,
  buildRootUrl,
} from "./Api_url";
import {
  TelegramLaunchContext,
  TelegramLaunchIssue,
  getTelegramLaunchContext,
} from "./telegramWebApp";
import {
  appendStoredReferralParams,
  getStoredReferralPayload,
  persistReferralParamsFromSearch,
} from "./referralParams";

type AuthMethod = "telegram" | "password" | "session" | null;
type AuthStatus =
  | "bootstrapping"
  | "telegram_authenticating"
  | "telegram_reauthenticating"
  | "authenticated"
  | "stored_session_restored"
  | "password_required"
  | "unauthenticated";
type AuthFallbackReason =
  | "none"
  | "browser_entry"
  | "stored_session_restored"
  | "telegram_sdk_missing"
  | "telegram_webapp_missing"
  | "telegram_init_missing"
  | "telegram_auth_failed"
  | "session_expired"
  | "manual_logout";
type TelegramAuthOptions = {
  clearOnFailure?: boolean;
  setErrorOnFailure?: boolean;
  statusOnStart?: AuthStatus;
  statusOnFailure?: AuthStatus;
  fallbackReasonOnFailure?: AuthFallbackReason;
};

interface SessionContextType {
  isAuthenticated: boolean;
  sessionId: string | null;
  loading: boolean;
  bootLoading: boolean;
  authResolved: boolean;
  authMethod: AuthMethod;
  authStatus: AuthStatus;
  authFallbackReason: AuthFallbackReason;
  telegramLaunchContext: TelegramLaunchContext;
  error: string | null;

  setSession: (sessionId: string, method?: AuthMethod) => void;
  setLoading: (value: boolean) => void;
  setError: (value: string | null) => void;

  authorizedFetch: (
    input: RequestInfo | URL,
    init?: RequestInit
  ) => Promise<Response>;
  loginWithTelegram: () => Promise<boolean>;
  reauthorizeWithTelegram: (
    options?: TelegramAuthOptions
  ) => Promise<string | null>;
  loginWithPassword: (login: string, password: string) => Promise<boolean>;
  logout: (reason?: AuthFallbackReason) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

const SESSION_STORAGE_KEY = "session_id";
const AUTH_METHOD_STORAGE_KEY = "auth_method";
const MANUAL_LOGOUT_STORAGE_KEY = "hydra_manual_logout";
const SESSION_URL_PARAM_KEY = "session_id";
const SESSION_EXPIRED_MESSAGE =
  "Сессия недействительна. Выполните вход повторно";

const extractSessionId = (data: any): string | null => {
  return data?.session_id || data?.sessionId || data?.sid || null;
};

const createSessionError = () => {
  const error: Error & { status?: number } = new Error(SESSION_EXPIRED_MESSAGE);
  error.status = 401;
  return error;
};

// A forbidden operation (403) does not invalidate an authenticated session.
const isUnauthorizedStatus = (status?: number) => status === 401;

const withSessionHeader = (headersInit: HeadersInit | undefined, sessionId: string) => {
  const headers = new Headers(headersInit);
  headers.set("session-id", sessionId);
  return headers;
};

const readSessionIdFromSearch = (search: string): string | null => {
  const rawValue = new URLSearchParams(search).get(SESSION_URL_PARAM_KEY);
  const sessionId = rawValue?.trim();

  return sessionId ? sessionId : null;
};

const removeSessionIdFromCurrentUrl = () => {
  const url = new URL(window.location.href);

  if (!url.searchParams.has(SESSION_URL_PARAM_KEY)) {
    return;
  }

  url.searchParams.delete(SESSION_URL_PARAM_KEY);

  const nextSearch = url.searchParams.toString();
  const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`;

  window.history.replaceState(window.history.state, "", nextUrl);
};

const mapIssueToFallbackReason = (
  issue: TelegramLaunchIssue
): AuthFallbackReason => {
  if (issue === "sdk_missing") return "telegram_sdk_missing";
  if (issue === "webapp_missing") return "telegram_webapp_missing";
  if (issue === "init_data_missing") return "telegram_init_missing";

  return "none";
};

const getTelegramLaunchErrorMessage = (issue: TelegramLaunchIssue) => {
  if (issue === "sdk_missing") {
    return "Не удалось загрузить Telegram WebApp. Откройте приложение снова из Telegram.";
  }

  if (issue === "webapp_missing") {
    return "Telegram WebApp недоступен. Попробуйте открыть приложение повторно из Telegram.";
  }

  if (issue === "init_data_missing") {
    return "Telegram не передал данные для входа. Попробуйте открыть приложение повторно из Telegram.";
  }

  return "Автоматический вход через Telegram не выполнен";
};

const isBootstrapBypassPath = (pathname: string) => {
  const normalizedPath = pathname.trim().replace(/\/+$/, "") || "/";
  return normalizedPath === "/authhapp" || normalizedPath === "/infohapp";
};

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [bootLoading, setBootLoading] = useState<boolean>(true);
  const [authResolved, setAuthResolved] = useState<boolean>(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>("bootstrapping");
  const [authFallbackReason, setAuthFallbackReason] =
    useState<AuthFallbackReason>("none");
  const [telegramLaunchContext, setTelegramLaunchContext] =
    useState<TelegramLaunchContext>(() => getTelegramLaunchContext());
  const [error, setError] = useState<string | null>(null);
  const telegramAuthPromiseRef = useRef<Promise<string | null> | null>(null);
  const telegramAuthVersionRef = useRef(0);

  const syncTelegramLaunchContext = useCallback(() => {
    const context = getTelegramLaunchContext();
    setTelegramLaunchContext(context);
    return context;
  }, []);

  const setSession = useCallback(
    (id: string, method: AuthMethod = "telegram") => {
      setSessionId(id);
      setAuthMethod(method);
      setAuthStatus("authenticated");
      setAuthFallbackReason("none");
      sessionStorage.removeItem(MANUAL_LOGOUT_STORAGE_KEY);
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
      sessionStorage.setItem(AUTH_METHOD_STORAGE_KEY, method ?? "");
    },
    []
  );

  const clearSession = useCallback(() => {
    setSessionId(null);
    setAuthMethod(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_METHOD_STORAGE_KEY);
  }, []);

  const restoreStoredSession = useCallback(
    (
      storedSession: string,
      storedMethod: AuthMethod | null = null,
      reason: AuthFallbackReason = "stored_session_restored"
    ) => {
      setSessionId(storedSession);
      setAuthMethod(storedMethod || null);
      setAuthStatus("stored_session_restored");
      setAuthFallbackReason(reason);
    },
    []
  );

  const performTelegramAuth = useCallback(
    async (
      options: TelegramAuthOptions = {}
    ): Promise<string | null> => {
      const {
        clearOnFailure = true,
        setErrorOnFailure = true,
        statusOnStart = "telegram_authenticating",
        statusOnFailure = "password_required",
        fallbackReasonOnFailure = "telegram_auth_failed",
      } = options;

      if (telegramAuthPromiseRef.current) {
        return telegramAuthPromiseRef.current;
      }

      const requestVersion = ++telegramAuthVersionRef.current;
      const authPromise = Promise.resolve().then(async () => {
        setLoading(true);
        setError(null);
        setAuthStatus(statusOnStart);
        setAuthFallbackReason("none");

        try {
          persistReferralParamsFromSearch(window.location.search);

          const launchContext = syncTelegramLaunchContext();

          if (!launchContext.canAuthorizeWithTelegram) {
            throw new Error(getTelegramLaunchErrorMessage(launchContext.issue));
          }

          const initData = window.Telegram?.WebApp?.initData?.trim();

          if (!initData) {
            throw new Error(
              getTelegramLaunchErrorMessage("init_data_missing")
            );
          }

          const query = new URLSearchParams();
          query.append("profile", "telegram_bot");
          query.append("initData", initData);
          appendStoredReferralParams(query);

          const response = await fetch(buildRootUrl(AUTH_TG_URL, query.toString()), {
            method: "GET",
          });

          if (!response.ok) {
            throw new Error(`Ошибка авторизации: ${response.status}`);
          }

          const data = await response.json();
          const sid = extractSessionId(data);

          if (!sid) {
            throw new Error("Сервер не вернул session_id");
          }

          if (requestVersion !== telegramAuthVersionRef.current) return null;
          setSession(sid, "telegram");
          return sid;
        } catch (err: any) {
          if (requestVersion !== telegramAuthVersionRef.current) return null;
          const launchContext = syncTelegramLaunchContext();
          const fallbackReason =
            launchContext.issue !== "none"
              ? mapIssueToFallbackReason(launchContext.issue)
              : fallbackReasonOnFailure;

          setAuthStatus(statusOnFailure);
          setAuthFallbackReason(fallbackReason);

          if (setErrorOnFailure) {
            setError(err?.message || "Ошибка Telegram авторизации");
          }

          if (clearOnFailure) {
            clearSession();
          }

          return null;
        } finally {
          if (requestVersion === telegramAuthVersionRef.current) {
            setLoading(false);
            telegramAuthPromiseRef.current = null;
          }
        }
      });

      telegramAuthPromiseRef.current = authPromise;
      return authPromise;
    },
    [clearSession, setSession, syncTelegramLaunchContext]
  );

  const loginWithTelegram = useCallback(async (): Promise<boolean> => {
    const sid = await performTelegramAuth({
      statusOnStart: "telegram_authenticating",
      statusOnFailure: "password_required",
      fallbackReasonOnFailure: "telegram_auth_failed",
    });

    return Boolean(sid);
  }, [performTelegramAuth]);

  const reauthorizeWithTelegram = useCallback(
    async (options: TelegramAuthOptions = {}): Promise<string | null> => {
      if (sessionStorage.getItem(MANUAL_LOGOUT_STORAGE_KEY) === "1") return null;
      return performTelegramAuth({
        clearOnFailure: false,
        setErrorOnFailure: false,
        statusOnStart: "telegram_reauthenticating",
        statusOnFailure: "password_required",
        fallbackReasonOnFailure: "session_expired",
        ...options,
      });
    },
    [performTelegramAuth]
  );

  const loginWithPassword = useCallback(
    async (login: string, password: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      setAuthStatus("password_required");

      const trimmedLogin = login.trim();
      const trimmedPassword = password.trim();

      if (!trimmedLogin || !trimmedPassword) {
        setError("Введите логин и пароль");
        setLoading(false);
        return false;
      }

      try {
        persistReferralParamsFromSearch(window.location.search);

        const response = await fetch(buildRootUrl(AUTH_URL), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            login: trimmedLogin,
            password: trimmedPassword,
            ...getStoredReferralPayload(),
          }),
        });

        if (response.status === 401) {
          throw new Error("Неверный логин или пароль");
        }

        if (!response.ok) {
          throw new Error(`Ошибка авторизации: ${response.status}`);
        }

        const data = await response.json();
        const sid = extractSessionId(data);

        if (!sid) {
          throw new Error("Сервер не вернул session_id");
        }

        setSession(sid, "password");
        return true;
      } catch (err: any) {
        setError(err?.message || "Ошибка входа");
        clearSession();
        setAuthStatus("password_required");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [clearSession, setSession]
  );

  const logout = useCallback(
    (reason: AuthFallbackReason = "manual_logout") => {
      if (reason === "manual_logout") {
        sessionStorage.setItem(MANUAL_LOGOUT_STORAGE_KEY, "1");
      }
      telegramAuthVersionRef.current += 1;
      telegramAuthPromiseRef.current = null;
      clearSession();
      setError(null);
      setLoading(false);
      setBootLoading(false);
      setAuthResolved(true);
      setAuthFallbackReason(reason);
      setAuthStatus(
        reason === "manual_logout" ? "unauthenticated" : "password_required"
      );
    },
    [clearSession]
  );

  const authorizedFetch = useCallback(
    async (input: RequestInfo | URL, init: RequestInit = {}) => {
      if (!sessionId) {
        throw createSessionError();
      }

      const executeRequest = async (activeSessionId: string) => {
        const response = await fetch(input, {
          ...init,
          headers: withSessionHeader(init.headers, activeSessionId),
        });

        return response;
      };

      let response = await executeRequest(sessionId);

      if (!isUnauthorizedStatus(response.status)) {
        return response;
      }

      const launchContext = syncTelegramLaunchContext();

      if (launchContext.canAuthorizeWithTelegram) {
        const refreshedSessionId = await reauthorizeWithTelegram({
          clearOnFailure: false,
          setErrorOnFailure: false,
          statusOnStart: "telegram_reauthenticating",
          statusOnFailure: "password_required",
          fallbackReasonOnFailure: "session_expired",
        });

        if (refreshedSessionId) {
          response = await executeRequest(refreshedSessionId);

          if (!isUnauthorizedStatus(response.status)) {
            return response;
          }
        }
      }

      logout("session_expired");
      throw createSessionError();
    },
    [logout, reauthorizeWithTelegram, sessionId, syncTelegramLaunchContext]
  );

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      persistReferralParamsFromSearch(window.location.search);

      const sessionIdFromUrl = readSessionIdFromSearch(window.location.search);
      const shouldBypassBootstrapAuth = isBootstrapBypassPath(window.location.pathname);

      if (sessionIdFromUrl) {
        sessionStorage.removeItem(MANUAL_LOGOUT_STORAGE_KEY);
        sessionStorage.setItem(SESSION_STORAGE_KEY, sessionIdFromUrl);
        sessionStorage.setItem(AUTH_METHOD_STORAGE_KEY, "session");
        removeSessionIdFromCurrentUrl();
      }

      const storedSession =
        sessionIdFromUrl ?? sessionStorage.getItem(SESSION_STORAGE_KEY);
      const storedMethod = sessionStorage.getItem(
        AUTH_METHOD_STORAGE_KEY
      ) as AuthMethod | null;

      try {
        setBootLoading(true);
        setError(null);
        setAuthStatus("bootstrapping");

        if (sessionIdFromUrl) {
          restoreStoredSession(sessionIdFromUrl, "session");
          return;
        }

        if (sessionStorage.getItem(MANUAL_LOGOUT_STORAGE_KEY) === "1") {
          clearSession();
          setAuthStatus("unauthenticated");
          setAuthFallbackReason("manual_logout");
          return;
        }

        if (shouldBypassBootstrapAuth) {
          clearSession();
          setAuthStatus("unauthenticated");
          setAuthFallbackReason("browser_entry");
          return;
        }

        const launchContext = syncTelegramLaunchContext();

        if (launchContext.canAuthorizeWithTelegram) {
          const telegramSessionId = await reauthorizeWithTelegram({
            clearOnFailure: false,
            setErrorOnFailure: !storedSession,
            statusOnStart: "telegram_authenticating",
            statusOnFailure: storedSession
              ? "stored_session_restored"
              : "password_required",
            fallbackReasonOnFailure: "telegram_auth_failed",
          });

          if (telegramSessionId) {
            return;
          }

          if (storedSession) {
            restoreStoredSession(
              storedSession,
              storedMethod || null,
              "telegram_auth_failed"
            );
            return;
          }

          setAuthStatus("password_required");
          setAuthFallbackReason("telegram_auth_failed");
          return;
        }

        if (launchContext.isLikelyTelegram && launchContext.issue !== "none") {
          const fallbackReason = mapIssueToFallbackReason(launchContext.issue);

          if (storedSession) {
            restoreStoredSession(
              storedSession,
              storedMethod || null,
              fallbackReason
            );
            return;
          }

          setAuthStatus("password_required");
          setAuthFallbackReason(fallbackReason);
          return;
        }

        if (storedSession) {
          restoreStoredSession(storedSession, storedMethod || null);
          return;
        }

        setAuthStatus("password_required");
        setAuthFallbackReason("browser_entry");
      } finally {
        if (mounted) {
          setBootLoading(false);
          setAuthResolved(true);
        }
      }
    };

    bootstrap();

    return () => {
      mounted = false;
    };
  }, [
    clearSession,
    reauthorizeWithTelegram,
    restoreStoredSession,
    syncTelegramLaunchContext,
  ]);

  const value = useMemo(
    () => ({
      isAuthenticated: !!sessionId,
      sessionId,
      loading,
      bootLoading,
      authResolved,
      authMethod,
      authStatus,
      authFallbackReason,
      telegramLaunchContext,
      error,
      setSession,
      setLoading,
      setError,
      authorizedFetch,
      loginWithTelegram,
      reauthorizeWithTelegram,
      loginWithPassword,
      logout,
    }),
    [
      sessionId,
      loading,
      bootLoading,
      authResolved,
      authMethod,
      authStatus,
      authFallbackReason,
      telegramLaunchContext,
      error,
      setSession,
      authorizedFetch,
      loginWithTelegram,
      reauthorizeWithTelegram,
      loginWithPassword,
      logout,
    ]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
